import { LabelNode, RuleContext, TemplateLabel } from '@navstation-portal/reporting/data-model/ui';
import { evaluate } from './evaluate';

/**
 * Resolves a template label to the text the form shows.
 *
 * A plain string resolves to itself. A label node resolves through the label
 * tree, which asks its questions with the rule tree.
 *
 * It returns undefined when the template gives no text, so the caller falls
 * back to the text in its own markup. That covers two shapes: no label at all,
 * and a `when` node whose rule is false with no `else`. Both mean the template
 * has nothing to say, so the markup speaks.
 *
 * An empty string is not undefined. It is an instruction to show no label text,
 * so callers use `??` and never `||`.
 */
export function resolveLabel(label: TemplateLabel | undefined, context: RuleContext): string | undefined {
  if (label === undefined) {
    return undefined;
  }
  if (typeof label === 'string') {
    return label;
  }
  return resolveNode(label, context);
}

function resolveNode(node: LabelNode, context: RuleContext): string | undefined {
  switch (node.type) {
    case 'text':
      return node.value;
    case 'when': {
      const branch = evaluate(node.when, context) ? node.then : node.else;
      return branch === undefined ? undefined : resolveNode(branch, context);
    }
    default:
      // Every node type has a branch above. This guards a new type added
      // without a branch: the compiler fails here.
      return assertNever(node);
  }
}

function assertNever(node: never): never {
  throw new Error(`Unknown label node type: ${String((node as { type: string }).type)}`);
}
